import React from "react";
// import HostLandingLayout from "../../layout/host-landing-layout-desktop/host-landing-layout";
import { toAbsoluteUrl } from "../../layout/helpers";
import { motion } from "framer-motion";
import "./styles.css";
import SEO from "../../components/SEO/SEO";
import logger from "../../utils/logger";

// export type HostLandingWebProps = {};

// eslint-disable-next-line no-empty-pattern
export default function HostLandingWeb() {
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [msisdn, setMsisdn] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [message, setMessage] = React.useState("");

  const [isEmptyFirstname, setIsEmptyFirstname] = React.useState(false);
  const [isEmptyLastname, setIsEmptyLastname] = React.useState(false);
  const [isEmptyPhoneNumber, setIsEmptyPhoneNumber] = React.useState(false);
  const [isEmptyEmail, setIsEmptyEmail] = React.useState(false);
  const [isEmptyMessage, setIsEmptyMessage] = React.useState(false);

  const hasText = (text: string) => {
    return text != null && text !== "";
  };

  const areFieldsOkay = () => {
    let valid = true;

    if (!hasText(firstName)) {
      valid = false;
      setIsEmptyFirstname(true);
    }

    if (!hasText(lastName)) {
      valid = false;
      setIsEmptyLastname(true);
    }

    if (!hasText(msisdn)) {
      valid = false;
      setIsEmptyPhoneNumber(true);
    }

    if (!hasText(email)) {
      valid = false;
      setIsEmptyEmail(true);
    }

    if (!hasText(message)) {
      valid = false;
      setIsEmptyMessage(true);
    }

    return valid;
  };

  const submitEnquiry = (event: any): void => {
    event.preventDefault();
    if (areFieldsOkay()) {
      // Submit form logic would go here
      logger.log("Form submitted successfully");
      // Reset form fields
      setFirstName("");
      setLastName("");
      setMsisdn("");
      setEmail("");
      setMessage("");
    }
  };

  return (
    <>
      <SEO
        title="Become a Host on Spacia | List Your Space & Earn Revenue"
        description="Join Spacia as a host and list your workspace, accommodation, or experience. Reach thousands of professionals across Africa. Easy setup, flexible pricing, and secure payments."
        keywords={[
          "spacia host",
          "list workspace",
          "become host",
          "rent space",
          "host platform",
          "space listing",
          "earn from space",
        ]}
        url={typeof window !== "undefined" ? `${window.location.origin}/host-experience` : ""}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Become a Host on Spacia",
          description: "Join Spacia as a host and list your workspace, accommodation, or experience",
          url: typeof window !== "undefined" ? `${window.location.origin}/host-experience` : "",
        }}
      />
      {/* Hero Section */}
      <section className="relative pt-32 md:pt-40 pb-8 md:pb-12 px-4 md:px-6 lg:px-8">
        {/* Decorative elements */}
        <div className="hidden md:block absolute top-[11%] left-0 z-10">
          <img src={toAbsoluteUrl("/img/pc-left.svg")} alt="Decorative left" className="w-24 h-auto" />
        </div>
        <div className="hidden md:block absolute -top-[336px] right-0 z-10">
          <img src={toAbsoluteUrl("/img/pc-right.svg")} alt="Decorative right" className="w-24 h-auto" />
        </div>

        {/* Main content */}
        <div className="container mx-auto px-4 md:px-6">
          {/* Hero section with image grid */}
          <div className="mb-12 md:mb-16">
            <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
              <div className="w-full md:w-auto">
                <img
                  className="w-full md:w-[22vw] max-w-full md:max-w-[193px] h-[259px] rounded-[12px] md:rounded-[20px] object-cover object-center mt-auto"
                  src={toAbsoluteUrl("/img/rec1.png")}
                  alt="Modern workspace"
                />
              </div>
              <div className="w-full md:w-auto">
                <img
                  className="w-full md:w-[22vw] max-w-full md:max-w-[193px] h-[346px] rounded-[12px] md:rounded-[20px] object-cover object-center mt-4 md:mt-auto"
                  src={toAbsoluteUrl("/img/rec2.png")}
                  alt="Office space"
                />
              </div>
              <div className="ml-0 md:ml-2 overflow-hidden mt-6 md:mt-0">
                <h1 className="mb-2 text-4xl md:text-6xl lg:text-[80px] font-bold leading-tight">
                  Let Your Space Earn Smarter
                </h1>
                <p className="text-start mt-2 lg:mt-3 text-base md:text-lg lg:text-xl w-full md:w-[50vw] max-w-[695px] leading-relaxed md:leading-relaxed">
                  With an increased demand for flexible workspace, now is the time to capitalise on your unused space.
                  Broaden your tenant prospects by listing on Spacia, and grow your earnings
                </p>
              </div>
            </div>
          </div>

          {/* Large image containers */}
          <div className="flex flex-col md:flex-row justify-center gap-6 md:gap-12 mb-12 md:mb-16">
            <img
              className="w-full md:w-[521px] h-auto md:h-[346px] rounded-[12px] md:rounded-[20px] object-cover object-center"
              src={toAbsoluteUrl("/img/rec3.png")}
              alt="Office space"
            />
            <img
              className="w-full md:w-[521px] h-auto md:h-[346px] rounded-[12px] md:rounded-[20px] object-cover object-center md:-translate-y-10"
              src={toAbsoluteUrl("/img/rec4.png")}
              alt="Meeting space"
            />
          </div>
        </div>
      </section>

      {/* Network section */}
      <section className="py-8 md:py-12 px-4 md:px-6 lg:px-8 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 w-full md:w-3/4">
            Access our dynamic network of corporate partners
          </h2>
          <p className="text-lg md:text-xl lg:text-2xl leading-relaxed mb-8 md:mb-12">
            On Spacia, you have the flexibility to choose how your space works for you. From listing a few workstations,
            a serviced office, event space or hotel rooms, we give you the freedom to conveniently showcase your space.
          </p>

          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className="w-full md:w-1/2">
              <img
                src={toAbsoluteUrl("/img/user-viewing.png")}
                alt="User viewing space"
                className="w-full max-w-[569px] h-auto md:h-[563px] object-cover object-left rounded-[12px] md:rounded-3xl"
              />
            </div>

            <div className="w-full md:w-1/2 flex flex-col items-start">
              <h3 className="py-4 text-black font-medium text-2xl md:text-3xl lg:text-[min(50px,3vw)]">
                Put your space to&nbsp;
                <span className="text-[#00A3FF] font-medium">work</span>
              </h3>

              <ol className="list-decimal ml-5 space-y-6">
                <li>
                  <div className="flex flex-col">
                    <h4 className="text-2xl md:text-3xl text-black font-medium">Sign Up</h4>
                    <span className="text-gray-600 text-lg md:text-2xl">Set up your free host account</span>
                  </div>
                </li>
                <li>
                  <div className="flex flex-col">
                    <h4 className="text-2xl md:text-3xl text-black font-medium">Get your space booked</h4>
                    <span className="text-gray-600 text-lg md:text-2xl">
                      We reserve space at your location for our pool of corporate clients
                    </span>
                  </div>
                </li>
                <li>
                  <div className="flex flex-col">
                    <h4 className="text-2xl md:text-3xl text-black font-medium">Earn</h4>
                    <span className="text-gray-600 text-lg md:text-2xl">
                      Check your bookings and track your earnings on the platform
                    </span>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Tools section */}
      <section className="py-8 md:py-12 px-4 md:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="w-24 h-1 bg-blue-500 mb-4" />
          <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-8 text-black">
            The right tools for the job.
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Tool 1 */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <img src={toAbsoluteUrl("/img/calender.svg")} alt="Calendar" className="w-8 h-8" />
              </div>
              <span className="text-lg md:text-xl font-medium text-[#2E0D64]">Integrated Calendar</span>
            </div>

            {/* Tool 2 */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <img src={toAbsoluteUrl("/img/qrCode.svg")} alt="QR Code" className="w-8 h-8" />
              </div>
              <span className="text-lg md:text-xl font-medium text-[#2E0D64]">QR Code Check In</span>
            </div>

            {/* Tool 3 */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                <img src={toAbsoluteUrl("/img/analytics.svg")} alt="Analytics" className="w-8 h-8" />
              </div>
              <span className="text-lg md:text-xl font-medium text-[#2E0D64]">Booking Analytics</span>
            </div>

            {/* Tool 4 */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center">
                <img src={toAbsoluteUrl("/img/notification.svg")} alt="Notification" className="w-8 h-8" />
              </div>
              <span className="text-lg md:text-xl font-medium text-[#2E0D64]">Real Time Notifications</span>
            </div>
          </div>
        </div>
      </section>

      {/* Available Now section with mobile app links */}
      <section className="bg-gradient-to-r from-blue-500 to-blue-400 py-16 md:py-24 px-4 md:px-8 relative overflow-hidden min-h-[100vh] flex items-center">
        {/* Decorative background circles */}
        <div className="absolute w-[400px] h-[400px] bg-white/10 rounded-full -top-[100px] -right-[100px] z-0" />
        <div className="absolute w-[300px] h-[300px] bg-white/5 rounded-full -bottom-[50px] -left-[50px] z-0" />

        <div className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 relative z-10 px-4 md:px-6">
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h2 className="mt-4 pb-2 mb-4 text-4xl md:text-5xl font-bold text-black leading-tight">
              Available <span className="text-white">Now</span>
            </h2>

            <p className="pb-8 text-lg md:text-xl leading-relaxed text-[#18191F] max-w-[600px]">
              Search, compare & book flexible workspaces, accommodation and other lifestyle services from the
              convenience of the Spacia app
            </p>

            <div>
              <h3 className="font-medium text-xl mb-3">Get the App</h3>
              <div className="flex flex-row gap-3 pb-5">
                <motion.a
                  href="https://play.google.com/store/apps/details?id=com.lucid_array.spacia.individual&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-black rounded-lg py-2 px-3 min-w-[160px] transition duration-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="flex items-center">
                    <img className="mr-2 w-6 h-6" alt="Google Play logo" src={toAbsoluteUrl("/img/google-play.png")} />
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-white uppercase">Get it on</span>
                      <span className="font-bold text-white text-base">Google Play</span>
                    </div>
                  </div>
                </motion.a>

                <motion.a
                  href="https://apps.apple.com/gh/app/spacia-work-stay-play/id6739746297"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-black rounded-lg py-2 px-3 min-w-[160px] transition duration-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="flex items-center">
                    <img className="mr-2 w-6 h-6" alt="App Store logo" src={toAbsoluteUrl("/img/apple-logo.png")} />
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-white">Download on the</span>
                      <span className="font-bold text-white text-base">App Store</span>
                    </div>
                  </div>
                </motion.a>
              </div>
            </div>
          </motion.div>

          <div className="relative h-[75vh] lg:h-auto flex items-center justify-center">
            <motion.div className="sticky top-0 flex items-center justify-center">
              <div className="relative transform scale-125">
                <img
                  src={toAbsoluteUrl("/assets/img/Web/Spacia/phone-mockup.png")}
                  alt="Spacia mobile app"
                  className="h-auto max-w-full rounded-[12px]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Get in Touch contact section */}
      <section className="py-12 md:py-24 px-4 md:px-8 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Get in Touch</h2>
            <p className="text-lg text-gray-600 text-center mb-10">
              Have questions about listing your space? Fill out the form below and our team will get back to you.
            </p>

            <form onSubmit={submitEnquiry} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={e => {
                      setFirstName(e.target.value);
                      setIsEmptyFirstname(false);
                    }}
                    className={`w-full px-4 py-3 border ${isEmptyFirstname ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-blue-500 focus:border-blue-500`}
                    placeholder="Enter your first name"
                  />
                  {isEmptyFirstname && <p className="mt-1 text-sm text-red-500">Please enter your first name</p>}
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={e => {
                      setLastName(e.target.value);
                      setIsEmptyLastname(false);
                    }}
                    className={`w-full px-4 py-3 border ${isEmptyLastname ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-blue-500 focus:border-blue-500`}
                    placeholder="Enter your last name"
                  />
                  {isEmptyLastname && <p className="mt-1 text-sm text-red-500">Please enter your last name</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={e => {
                      setEmail(e.target.value);
                      setIsEmptyEmail(false);
                    }}
                    className={`w-full px-4 py-3 border ${isEmptyEmail ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-blue-500 focus:border-blue-500`}
                    placeholder="Enter your email"
                  />
                  {isEmptyEmail && <p className="mt-1 text-sm text-red-500">Please enter your email</p>}
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={msisdn}
                    onChange={e => {
                      setMsisdn(e.target.value);
                      setIsEmptyPhoneNumber(false);
                    }}
                    className={`w-full px-4 py-3 border ${isEmptyPhoneNumber ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-blue-500 focus:border-blue-500`}
                    placeholder="Enter your phone number"
                  />
                  {isEmptyPhoneNumber && <p className="mt-1 text-sm text-red-500">Please enter your phone number</p>}
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={e => {
                    setMessage(e.target.value);
                    setIsEmptyMessage(false);
                  }}
                  rows={4}
                  className={`w-full px-4 py-3 border ${isEmptyMessage ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-blue-500 focus:border-blue-500`}
                  placeholder="Tell us about your space"
                ></textarea>
                {isEmptyMessage && <p className="mt-1 text-sm text-red-500">Please enter your message</p>}
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  className="inline-flex items-center justify-center bg-blue-600 text-white px-8 py-3 rounded-full font-medium hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Submit Inquiry
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
